import React from 'react';
import * as S from './Styles';
import { getScoreIcons } from '../../../utils/scoreToIcons';
import { ChevronRightIcon, ChevronDownIcon, InfoIcon, ShoppingCartIcon } from '@fluentui/react-icons-mdl2';
import { translate } from '../../../utils/i18n';
import { ActiveIcon, InactiveIcon } from '../../icons/IconsTable';
import { TooltipHost, Callout, DelayedRender, Text } from '@fluentui/react';
import { useBoolean, useId } from "@fluentui/react-hooks";
import { longPercentFormatter } from "../../../utils/numberFormatter";
import { CustomCurrencyFormat } from "../../../utils/CustomCurrencyFormat";
import Auth from '../../../Auth/Auth';
import AppendCartLine from '../../../utils/AppendCartLine';
import { useDispatch, useSelector } from 'react-redux';
import { setCartSending } from '../../../store/cart/actions';
import { setCartSendingEP } from '../../../store/educationalcart/actions';

export interface Fund
{
    family: string;
    manager: string;
    category: string;
    averageScore: number;
    children: any[];
}

interface PrincipalTableProps
{
    groups: Fund[];
    columns: string[];
    publicVersion: boolean;
    theme: any;
    history: any;
    fromEF: boolean;
}

const SearchFundsPrincipalTable: React.FC<PrincipalTableProps> = ({groups, columns, publicVersion, theme, history, fromEF}) =>
{
    const dispatch = useDispatch();
    const isActiveManagementPlan: boolean = Auth.isActiveManagementPlan();
    const isUserSubscribed = Auth.isSubscribed();
    
    // Seleccionar el estado del carrito correcto según el tipo de usuario
    const cartLines = useSelector((state: any) => 
    {
        if(!isUserSubscribed)
        {
            return state.educationalCart.cartLines;
        }
        else
        {
            return state.cart.cartLines;
        }
    });
    
    const [expandedRows, setExpandedRows] = React.useState<{[key: number]: boolean}>({});
    const [isCalloutVisible, {setTrue: showCallout, setFalse: hideCallout}] = useBoolean(false);
    const [clipboardIsin, setClipboardIsin] = React.useState<string>("");
    const buttonId = useId('callout-button');

    React.useEffect(() =>
    {
        const timer = setTimeout(() =>
        {
            hideCallout();
        }, 1000);
        return () => clearTimeout(timer);
    }, [hideCallout, isCalloutVisible]);

    const getScoreCategory = (score: number): string =>
    {
        if(score >= 80) return "Excelente";
        if(score >= 60) return "Muy Bueno";
        if(score >= 40) return "Bueno";
        if(score >= 20) return "Regular";
        return "Mejorable";
    };

    const getScoreDescription = (score: number): string =>
    {
        if(score >= 80) return "Fondo capaz de sobresalir en su categoría analizando tanto su rentabilidad como su riesgo en múltiples periodos de tiempo.";
        if (score >= 60) return "Fondo de mucha calidad, con puntuación más alta en categorías complejas con muchos integrantes.";
        if(score >= 40) return "Fondo de buena calidad, con características de rentabilidad y riesgo consistentemente sólidas en plazos de 1 a 5 años respecto al resto de fondos de su categoría.";
        if(score >= 20) return "Puntuación inferior a la media de los fondos de su categoría en base a 17 métricas de rentabilidad y riesgo en plazos de 1 a 5 años.";
        return "Fondo que está por debajo de la media de los fondos de su categoría, con resultados desajustados entre su rentabilidad y riesgo en los últimos 5 años.";
    };

    const formatScoreWithComma = (score: number): string =>
    {
        return parseFloat(score.toString()).toFixed(2).replace('.', ',');
    };

    const renderScoreTooltip = (score: number): JSX.Element =>
    {
        const scoreValue = formatScoreWithComma(score);
        const scorePercentage = Math.min(score, 100);
        
        return(
            <S.ScoreTooltipContainer>
                <div className="score-header">
                    <span className="score-title">Puntuación IronIA</span>
                    <span className="score-badge">{scoreValue}/100</span>
                </div>
                <div className="score-bar-container">
                    <div className="score-label">{getScoreCategory(score)}</div>
                    <div className="score-bar">
                        <div className="score-fill" style={{width: `${scorePercentage}%`}} />
                    </div>
                </div>
                <div className="score-description">
                    {getScoreDescription(score)}
                </div>
            </S.ScoreTooltipContainer>
        );
    };

    // Función para verificar si un fondo ya está en el carrito
    const isFundInCart = (fundIsin: string): boolean =>
    {
        return cartLines.some((line: any) => line.fund.isin === fundIsin);
    };

    const addToCart = async (fund: any) =>
    {
        if(isFundInCart(fund.isin))
        {
            return;
        }
        
        if(!isUserSubscribed)
        {
            dispatch(setCartSendingEP(true));
        }
        else
        {
            dispatch(setCartSending(true));
        }
        
        await AppendCartLine(dispatch, history,
        {
            fund: fund,
            amount: 0
        });
        
        setTimeout(() =>
        {
            if(!isUserSubscribed)
            {
                dispatch(setCartSendingEP(false));
            }
            else
            {
                dispatch(setCartSending(false));
            }
        }, 1000);
    };

    return(
        <S.TableStyled theme={theme}>
            <thead>
                <tr>
                    {columns && columns.length > 0 && columns.map((column: string, columnIndex: number) =>
                    {
                        return(
                            <th style={{...(column === 'Nombre' ? {paddingLeft: 20} : {}), cursor: "default"}} key={'columnIndex' + columnIndex}>
                                <div>{column}</div>
                            </th>
                        );
                    })}
                </tr>
            </thead>
            <tbody>
                {groups && groups.length > 0 && groups.map((row: Fund, index: number) =>
                {
                    const isExpanded = expandedRows[index] || false;
                    const iconArray = row.children && row.children.length > 0 ? getScoreIcons(row.children[0].averageScore) : [];
                    
                    const toggleExpanded = () =>
                    {
                        setExpandedRows(prev =>
                        ({
                            ...prev,
                            [index]: !prev[index]
                        }));
                    };

                    return(
                        <React.Fragment key={'index' + index}>
                            <tr style={{cursor: row.children?.length > 0 ? 'pointer' : 'default'}} onClick={row.children?.length > 0 ? toggleExpanded : undefined}>
                                <td>
                                    <S.FundName className="max-content">
                                        <button style={{marginLeft: "20px", color: theme.palette.black}} className="expanded" onClick={(e) => 
                                        {
                                            e.stopPropagation();
                                            toggleExpanded();
                                        }}>
                                            {row.children?.length > 0 && (isExpanded
                                                ? <ChevronDownIcon className="brothers-btn-icon" />
                                                : <ChevronRightIcon className="brothers-btn-icon" />
                                            )}
                                            {row.family}
                                            {row.children?.length > 0 && ` (${row.children?.length})`}
                                        </button>
                                    </S.FundName>
                                </td>
                                <td>
                                    {row.children && row.children.length > 0 &&
                                    (
                                        <p className="max-content" style={{cursor: "default"}}>{translate(row.children[0].manager)}</p>
                                    )}
                                </td>
                                <td>
                                    {row.children && row.children.length > 0 &&
                                    (
                                        <p className="max-content" style={{cursor: "default"}}>{row.children[0].subcategory}</p>
                                    )}
                                </td>
                                <td onClick={(e) => e.stopPropagation()}>
                                    {row.children && row.children.length > 0 &&
                                    (
                                        <S.ScoreTooltipHost content={renderScoreTooltip(row.children[0].averageScore)}
                                            calloutProps={
                                            {
                                                className: 'score-tooltip-callout'
                                            }}
                                        >
                                            <div className="max-content" style={{cursor: 'pointer'}}>
                                                {iconArray.map((active, iconIndex) =>
                                                {
                                                    return active ? <ActiveIcon key={'iconIndex' + iconIndex} /> : <InactiveIcon key={'iconIndex' + iconIndex} />;
                                                })}
                                            </div>
                                        </S.ScoreTooltipHost>
                                    )}
                                </td>
                            </tr>
                            {isExpanded &&
                            (
                                <tr className="container-table">
                                    <td colSpan={4}>
                                        <S.SubTableInternalStyled>
                                            <S.FundInternalTableStyled>
                                                <thead>
                                                    <tr>
                                                        <th>
                                                            <div style={{marginLeft: "5px"}}>Nombre/ISIN</div>
                                                        </th>
                                                        <th>
                                                            <div>Divisa</div>
                                                        </th>
                                                        <th>
                                                            <div>Gastos Corrientes (OGC)</div>
                                                        </th>
                                                        <th>
                                                            <div>Retrocesión</div>
                                                        </th>
                                                        <th>
                                                            <div>Inversión Inicial Mínima</div>
                                                        </th>
                                                        <th>
                                                            <div>Dividendos</div>
                                                        </th>
                                                        <th>
                                                            <TooltipHost className="ironia-tooltip" content="Esta columna refleja la aplicación del mínimo de manera genérica para la clase del fondo. Puede que en una orden concreta sí se aplique.">
                                                                <div>
                                                                    Aplica Mínimo <InfoIcon />
                                                                </div>
                                                            </TooltipHost>
                                                        </th>
                                                        <th>
                                                            <div>Clase Limpia</div>
                                                        </th>
                                                        <th>
                                                            <div>Disponible</div>
                                                        </th>
                                                        <th>
                                                            <div />
                                                        </th>
                                                        {!isActiveManagementPlan && !fromEF &&
                                                        (
                                                            <th>
                                                                <div />
                                                            </th>
                                                        )}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {row.children && row.children.length > 0 && row.children.map((item, childIndex) =>
                                                    (
                                                        <React.Fragment key={childIndex}>
                                                            <tr key={item.key}>
                                                                <td>
                                                                    <S.FundCellContainer className="max-content"
                                                                        onClick={() =>
                                                                        {
                                                                            history.push(
                                                                            {
                                                                                pathname: (publicVersion ? "/iframe" : "") + `/${item.isin}`,
                                                                                state:
                                                                                {
                                                                                    fund: item,
                                                                                    brothers: row.children,
                                                                                    fromEF: fromEF
                                                                                }
                                                                            });
                                                                        }}
                                                                    >
                                                                        <S.FundNameRow>
                                                                            {item.name}
                                                                        </S.FundNameRow>
                                                                        <S.ISINContainer>
                                                                            <S.ISINRow>{item.isin}</S.ISINRow>
                                                                            <S.CopyButtonStyled id={buttonId + childIndex + item.key} className="compact"
                                                                                title={`Copia el ISIN ${item.isin} al portapapeles`}
                                                                                onClick={(e) =>
                                                                                {
                                                                                    e.stopPropagation();
                                                                                    setClipboardIsin(item.isin);
                                                                                    showCallout();
                                                                                    navigator.clipboard.writeText(item.isin);
                                                                                }}
                                                                            >
                                                                                <S.CompactCopyIcon />
                                                                            </S.CopyButtonStyled>
                                                                        </S.ISINContainer>
                                                                        {isCalloutVisible && clipboardIsin === item.isin &&
                                                                        (
                                                                            <Callout target={`#${buttonId + childIndex + item.key}`}
                                                                                onDismiss={hideCallout}
                                                                                isBeakVisible={false}
                                                                                role="alert"
                                                                            >
                                                                                <DelayedRender>
                                                                                    <Text variant="small">ISIN copiado</Text>
                                                                                </DelayedRender>
                                                                            </Callout>
                                                                        )}
                                                                    </S.FundCellContainer>
                                                                </td>
                                                                <td>{item.currency}</td>
                                                                <td>{longPercentFormatter.format(item.ongoingCharges/100)}</td>
                                                                <td>{longPercentFormatter.format(parseFloat(item.rebate)/100)}</td>
                                                                <td>
                                                                    {CustomCurrencyFormat(item.minimumInitialInvestment, item.currency)}
                                                                </td>
                                                                <td>{item.income ? "Sí" : "No"}</td>
                                                                <td>{item.noMin ? "Sí" : "No"}</td>
                                                                <td>{item.cleanShare ? "Sí" : "No"}</td>
                                                                <td>{item.available ? "Sí" : "No"}</td>
                                                                <td>
                                                                    <div className="max-content">
                                                                        {item.currency === "EUR" && item.cleanShare ? <ActiveIcon color="#1BB2AD" /> : ""}
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    {item.available && !isActiveManagementPlan && !fromEF &&
                                                                    (
                                                                        isFundInCart(item.isin) ? 
                                                                        (
                                                                            <TooltipHost content="Este fondo ya está en el carrito">
                                                                                <S.AddToCartButtonStyled
                                                                                    style={
                                                                                    {
                                                                                        backgroundColor: '#CDCDCD',
                                                                                        color: 'black',
                                                                                        cursor: 'default'
                                                                                    }}
                                                                                    disabled={true}
                                                                                >
                                                                                    <ShoppingCartIcon />
                                                                                </S.AddToCartButtonStyled>
                                                                            </TooltipHost>
                                                                        ) :
                                                                        (
                                                                            <TooltipHost content="Añade este Fondo al Carrito">
                                                                                <S.AddToCartButtonStyled
                                                                                    onClick={() =>
                                                                                    {
                                                                                        addToCart(item);
                                                                                    }}
                                                                                >
                                                                                    <ShoppingCartIcon />
                                                                                </S.AddToCartButtonStyled>
                                                                            </TooltipHost>
                                                                        )
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        </React.Fragment>
                                                    ))}
                                                </tbody>
                                            </S.FundInternalTableStyled>
                                        </S.SubTableInternalStyled>
                                    </td>
                                </tr>
                            )}
                        </React.Fragment>
                    );
                })}
            </tbody>
        </S.TableStyled>
    );
};

export default SearchFundsPrincipalTable;